import * as Yup from 'yup';
import { useState, useEffect } from 'react';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, Alert, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from '../../../redux/store';
// routes
import { getToken, hasError } from '../../../redux/slices/auth';

// hooks
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import useLocales from '../../../hooks/useLocales';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';

// ----------------------------------------------------------------------

const LoginForm = () => {
  const isMountedRef = useIsMountedRef();
  const [showPassword, setShowPassword] = useState(false);
  const { translate } = useLocales();
  const dispatch = useDispatch();
  const errorMessage = useSelector((state) => state.auth.error);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required(translate('Login.email_required')),
    password: Yup.string().required(translate('Login.password_required')),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await dispatch(getToken(data.email, data.password));
    } catch (error) {
      reset();
      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  useEffect(() => {
    if (errorMessage) {
      setTimeout(() => {
        dispatch(hasError(''));
      }, 5000);
    }
    localStorage.removeItem('accessToken');
  }, [errorMessage]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} sx={{ mb: 3 }}>
        {errorMessage ? (
          <Alert sx={{ mb: 1 }} severity="error">
            {errorMessage.error_message}
          </Alert>
        ) : (
          <></>
        )}

        <RHFTextField name="email" label={translate('Login.email')} />

        <RHFTextField
          name="password"
          label={translate('Login.password')}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        {translate('Login.login')}
      </LoadingButton>
    </FormProvider>
  );
};

export default LoginForm;
